import { useFieldArray, Control, UseFormRegister } from "react-hook-form";
import { Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

interface PaymentPlansEditorProps {
    control: Control<any>;
    register: UseFormRegister<any>;
    name: string;
}

export function PaymentPlansEditor({ control, register, name }: PaymentPlansEditorProps) {
    const { t } = useTranslation();
    const { fields, append, remove } = useFieldArray({
        control,
        name,
    });

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-slate-800">{t("paymentPlans.title", "Payment Plans")}</h3>
                <button
                    type="button"
                    onClick={() => append({
                        planCategory: "ORIGINAL_PLAN",
                        concurrentInstallment: 0,
                        installmentFrequency: "MONTHLY",
                        totalYears: 1,
                        downPayment: 0,
                        handoverPayment: 0
                    })}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary bg-primary/10 rounded-md hover:bg-primary/20"
                >
                    <Plus className="w-3.5 h-3.5" />
                    {t("paymentPlans.add", "Add Plan")}
                </button>
            </div>

            {fields.length === 0 && (
                <div className="text-center py-6 bg-slate-50 border border-dashed border-slate-200 rounded-lg">
                    <p className="text-sm text-slate-500">{t("paymentPlans.empty", "No payment plans added yet.")}</p>
                </div>
            )}

            <div className="space-y-4">
                {fields.map((field, index) => (
                    <div key={field.id} className="p-4 bg-slate-50 border border-slate-200 rounded-lg relative">
                        <button
                            type="button"
                            onClick={() => remove(index)}
                            className="absolute top-3 right-3 p-1.5 text-red-500 hover:bg-red-50 rounded-md transition-colors"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>

                        <div className="grid grid-cols-2 gap-4 mr-8">
                            <div>
                                <label className="block text-xs font-medium text-slate-500 mb-1">
                                    {t("paymentPlans.category", "Category")}
                                </label>
                                <select
                                    {...register(`${name}.${index}.planCategory`)}
                                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-md text-sm outline-none focus:ring-2 focus:ring-primary/20"
                                >
                                    <option value="ORIGINAL_PLAN">{t("paymentPlans.originalPlan", "Original Plan")}</option>
                                    <option value="OFFER_PLAN">{t("paymentPlans.offerPlan", "Offer Plan")}</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-slate-500 mb-1">
                                    {t("paymentPlans.frequency", "Frequency")}
                                </label>
                                <select
                                    {...register(`${name}.${index}.installmentFrequency`)}
                                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-md text-sm outline-none focus:ring-2 focus:ring-primary/20"
                                >
                                    <option value="MONTHLY">{t("paymentPlans.monthly", "Monthly")}</option>
                                    <option value="QUARTERLY">{t("paymentPlans.quarterly", "Quarterly")}</option>
                                    <option value="YEARLY">{t("paymentPlans.yearly", "Yearly")}</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-slate-500 mb-1">
                                    {t("paymentPlans.installment", "Installment Amount")}
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    step="any"
                                    {...register(`${name}.${index}.concurrentInstallment`, { valueAsNumber: true })}
                                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-md text-sm outline-none focus:ring-2 focus:ring-primary/20"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-slate-500 mb-1">
                                    {t("paymentPlans.duration", "Duration (Years)")}
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    {...register(`${name}.${index}.totalYears`, { valueAsNumber: true })}
                                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-md text-sm outline-none focus:ring-2 focus:ring-primary/20"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-slate-500 mb-1">
                                    {t("paymentPlans.downPayment", "Down Payment")}
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    step="any"
                                    {...register(`${name}.${index}.downPayment`, { valueAsNumber: true })}
                                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-md text-sm outline-none focus:ring-2 focus:ring-primary/20"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-slate-500 mb-1">
                                    {t("paymentPlans.handoverPayment", "Handover Payment (Residual)")}
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    step="any"
                                    {...register(`${name}.${index}.handoverPayment`, { valueAsNumber: true })}
                                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-md text-sm outline-none focus:ring-2 focus:ring-primary/20"
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
